import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@tutor/ui';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes so a bug in one screen shows a recoverable message
 * instead of unmounting the whole app to a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // TODO: forward to an error reporting service once one is wired up.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Something went wrong</h1>
        <p className="mt-2 max-w-md text-sm text-slate-500">
          The page hit an unexpected error. Reloading usually fixes it.
        </p>

        {import.meta.env.DEV && (
          <pre className="mt-4 max-w-lg overflow-auto rounded-lg bg-slate-900 p-4 text-left text-xs text-rose-300">
            {error.message}
          </pre>
        )}

        <Button className="mt-6" onClick={() => window.location.reload()}>
          Reload the page
        </Button>
      </div>
    );
  }
}
